import { useContext, useEffect, useRef, useState } from 'react'
import api from '../../../api'
import { ProjectContext } from '../../../contexts'
import { Campaign, CampaignCreateParams, CampaignType, InAppDeliveryMode, Provider, SearchParams, Subscription } from '../../../types'
import FormWrapper from '../../../ui/form/FormWrapper'
import { useTranslation } from 'react-i18next'
import { deepMerge } from '../../../utils'
import { UseFormReturn, useWatch } from 'react-hook-form'
import clsx from 'clsx'
import CampaignGeneralStep from './CampaignGeneralStep'
import CampaignAudienceStep from './CampaignAudienceStep'
import CampaignAdvancedStep from './CampaignAdvancedStep'
import CampaignWizardNav, { STEPS, WizardStep } from './CampaignWizardNav'
import './CampaignWizard.css'

const DEFAULT_IN_APP_DELIVERY: InAppDeliveryMode = 'individual'

interface CampaignEditParams {
    campaign?: Campaign
    onSave: (campaign: Campaign) => void
    type?: CampaignType
    folderId?: number
}

export function CampaignForm({ campaign, onSave, type, folderId }: CampaignEditParams) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [step, setStep] = useState<WizardStep>('general')

    const [providers, setProviders] = useState<Provider[]>([])
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
    useEffect(() => {
        const params: SearchParams = { limit: 9999, q: '' }
        api.subscriptions.search(project.id, params)
            .then(({ results }) => {
                setSubscriptions(results)
            })
            .catch(() => {})

        api.providers.all(project.id)
            .then((results) => {
                setProviders(results)
            })
            .catch(() => {})
    }, [])

    async function handleSave({
        name,
        type,
        list_ids,
        exclusion_list_ids,
        channel,
        provider_id,
        subscription_id,
        tags,
        settings,
    }: CampaignCreateParams) {
        const cleanSettings = settings
            ? {
                link_wrap: settings.link_wrap,
                priority: settings.priority,
                quiet_hours: settings.quiet_hours_enabled ? settings.quiet_hours : undefined,
                in_app_delivery: (campaign?.channel ?? channel) === 'in_app' && (campaign?.type ?? type) === 'blast'
                    ? settings.in_app_delivery
                    : undefined,
            }
            : settings
        const params = { name, list_ids, exclusion_list_ids, subscription_id, provider_id, tags, settings: cleanSettings }
        const value = campaign
            ? await api.campaigns.update(project.id, campaign.id, params)
            : await api.campaigns.create(project.id, { channel, type, folder_id: folderId, ...params })
        onSave(value)
    }

    const stepIndex = STEPS.indexOf(step)

    const stepLabels: Record<WizardStep, string> = {
        general: t('wizard_step_basics'),
        audience: t('wizard_step_audience'),
        advanced: t('wizard_step_advanced'),
    }

    return (
        <FormWrapper<CampaignCreateParams>
            onSubmit={async (item) => await handleSave(item)}
            defaultValues={deepMerge<Partial<Campaign>>({
                type: type ?? 'blast',
                channel: 'email',
                settings: {
                    link_wrap: project.link_wrap_email,
                    priority: 'none',
                    quiet_hours_enabled: !!campaign?.settings?.quiet_hours,
                    in_app_delivery: DEFAULT_IN_APP_DELIVERY,
                    quiet_hours: {
                        start: '22:00',
                        end: '08:00',
                        action: 'delay',
                    },
                },
            }, campaign)}
            submitLabel={campaign
                ? t('wizard_btn_save')
                : t('wizard_btn_create')}
            showSubmitButton={false}
        >
            {(form, metadata) => (
                <WizardContent
                    form={form}
                    metadata={metadata}
                    step={step}
                    setStep={setStep}
                    stepIndex={stepIndex}
                    stepLabels={stepLabels}
                    campaign={campaign}
                    type={type}
                    providers={providers}
                    subscriptions={subscriptions}
                    project={project}
                />
            )}
        </FormWrapper>
    )
}

interface WizardContentProps {
    form: UseFormReturn<CampaignCreateParams>
    metadata: {
        isLoading: boolean
        isValid: boolean
        disabled: boolean | undefined
    }
    step: WizardStep
    setStep: (step: WizardStep) => void
    stepIndex: number
    stepLabels: Record<WizardStep, string>
    campaign?: Campaign
    type?: CampaignType
    providers: Provider[]
    subscriptions: Subscription[]
    project: any
}

function WizardContent({
    form,
    metadata,
    step,
    setStep,
    stepIndex,
    stepLabels,
    campaign,
    type,
    providers,
    subscriptions,
    project,
}: WizardContentProps) {
    // Auto-set provider and subscription when data loads so validation
    // never fails on fields the user hasn't visited yet
    const channel = useWatch({ control: form.control, name: 'channel' })
    useEffect(() => {
        if (!channel) return
        const filteredProviders = providers.filter(
            p => p.group === channel || (channel === 'in_app' && p.group === 'push'),
        )
        if (filteredProviders.length) {
            const { provider_id } = form.getValues()
            if (!provider_id || !filteredProviders.find(p => p.id === provider_id)) {
                form.setValue('provider_id', filteredProviders[0].id)
            }
        }
        const filteredSubs = subscriptions.filter(s => s.channel === channel && !s.is_channel)
        if (filteredSubs.length) {
            const { subscription_id } = form.getValues()
            if (!subscription_id || !filteredSubs.find(s => s.id === subscription_id)) {
                form.setValue('subscription_id', filteredSubs[0].id)
            }
        }
    }, [channel, providers, subscriptions, form])

    // When the user switches channels, reset link_wrap to the project
    // default for that channel. Skips the initial render so an existing
    // campaign's saved setting isn't overwritten.
    const previousChannel = useRef(channel)
    useEffect(() => {
        if (previousChannel.current === channel) return
        previousChannel.current = channel
        form.setValue(
            'settings.link_wrap',
            channel === 'email'
                ? project.link_wrap_email
                : channel === 'push'
                    ? project.link_wrap_push
                    : channel === 'in_app'
                        ? project.link_wrap_in_app
                        : false,
        )
    }, [channel, form, project])

    const isLastStep = stepIndex === STEPS.length - 1

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isLastStep) {
            e.preventDefault()
            setStep(STEPS[stepIndex + 1])
        }
    }

    return (
        <div onKeyDown={handleKeyDown}>
            {/* Step Indicator */}
            <nav className="wizard-steps">
                {STEPS.map((s, i) => (
                    <button
                        key={s}
                        type="button"
                        className={clsx('wizard-step', {
                            active: s === step,
                            completed: i < stepIndex,
                        })}
                        disabled={i > stepIndex + 1}
                        onClick={() => {
                            if (i <= stepIndex + 1) setStep(s)
                        }}
                    >
                        <span className="wizard-step-label">{stepLabels[s]}</span>
                        <div className="wizard-step-bar" />
                    </button>
                ))}
            </nav>

            {/* Step 1: General */}
            {step === 'general' && (
                <CampaignGeneralStep
                    form={form}
                    campaign={campaign}
                    showType={!type}
                    subscriptions={subscriptions}
                    providers={providers}
                />
            )}

            {/* Step 2: Audience */}
            {step === 'audience' && (
                <CampaignAudienceStep
                    form={form}
                    campaign={campaign}
                />
            )}

            {/* Step 3: Advanced */}
            {step === 'advanced' && (
                <CampaignAdvancedStep
                    form={form}
                    providers={providers}
                    subscriptions={subscriptions}
                    project={project}
                />
            )}

            {/* Navigation */}
            <CampaignWizardNav
                step={step}
                form={form}
                campaign={campaign}
                metadata={metadata}
                onBack={() => setStep(STEPS[stepIndex - 1])}
                onNext={() => setStep(STEPS[stepIndex + 1])}
                hasProviders={providers.length > 0}
            />
        </div>
    )
}
